import { fetchWithTimeout } from "./http";
import { readApiResponse } from "./apiResponse";
import { dataSourceGetUrl, dataSourceRequestInit, isDataSourceConfigured, type UserDataSource } from "./dataSource";
import type { FriendCode, FriendCodeRegistration, FriendCodeTags, FriendCoordinateReference } from "../features/friends/types";

type RawFriendCode = Record<string, unknown>;
const FRIEND_REQUEST_TIMEOUT_MS = 30_000;

function parseJsonObject(value: unknown): Record<string, unknown> {
  if (value && typeof value === "object" && !Array.isArray(value)) return value as Record<string, unknown>;
  if (typeof value !== "string" || !value.trim()) return {};
  try {
    const parsed: unknown = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed as Record<string, unknown> : {};
  } catch {
    return {};
  }
}

function parseBoolean(value: unknown): boolean {
  if (value === true || value === 1) return true;
  return typeof value === "string" && ["true", "1", "yes", "checked"].includes(value.trim().toLowerCase());
}

function parseTags(value: unknown): FriendCodeTags {
  const parsed = parseJsonObject(value);
  const tags: FriendCodeTags = {};
  for (const [key, tagValue] of Object.entries(parsed)) {
    if (["boolean", "string", "number"].includes(typeof tagValue) || tagValue === null) {
      if (key === "not_web") {
        if (parseBoolean(tagValue)) tags.not_web = true;
      } else {
        tags[key] = tagValue as boolean | string | number | null;
      }
    }
  }
  return tags;
}

function parseCoordinate(type: unknown, id: unknown): FriendCoordinateReference {
  const coordinateId = String(id ?? "").trim();
  if (!coordinateId) return null;
  if (type === "existing" || type === "mixed") return { type, id: coordinateId };
  return null;
}

export function parseFriendCode(value: unknown): FriendCode | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const raw = value as RawFriendCode;
  const id = String(raw.id ?? "").trim();
  const playerId = String(raw.playerId ?? raw.player_id ?? "").trim();
  const playerName = String(raw.playerName ?? raw.player_name ?? "").trim();
  const qr = String(raw.qr ?? raw.data ?? "").trim();
  if (!id || !playerName || !qr) return null;
  return {
    id,
    playerId,
    playerName,
    photoSeq: String(raw.photo_seq ?? "").trim(),
    coordinate: parseCoordinate(raw.coordinateType ?? raw.coordinate_type, raw.coordinateId ?? raw.coordinate_id),
    qr,
    tags: parseTags(raw.tags),
    imageUrl: String(raw.imageUrl ?? raw.image_url ?? "").trim(),
    createdAt: String(raw.createdAt ?? raw.created_at ?? "").trim(),
  };
}

async function readPayload(response: Response): Promise<unknown> {
  const payload: unknown = await response.json().catch(() => null);
  if (payload && typeof payload === "object" && "error" in payload && payload.error) {
    throw new Error(String(payload.error));
  }
  return payload;
}

export async function fetchFriendCodes(source: UserDataSource): Promise<FriendCode[]> {
  if (!isDataSourceConfigured(source)) return [];
  const response = await fetchWithTimeout(dataSourceGetUrl(source, "friend-GET"), dataSourceRequestInit(source), FRIEND_REQUEST_TIMEOUT_MS);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const payload = await readPayload(response);
  if (!Array.isArray(payload)) throw new Error("friend-GET: 配列形式ではありません。");
  return payload.map(parseFriendCode).filter((code): code is FriendCode => code !== null);
}

export async function postFriendCode(
  source: UserDataSource,
  registration: FriendCodeRegistration,
  options: { id?: string; createdAt?: string } = {},
): Promise<FriendCode> {
  if (!isDataSourceConfigured(source)) throw new Error("保存先が設定されていません。");
  const playerId = registration.playerId.trim();
  const playerName = registration.playerName.trim();
  const photoSeq = registration.photoSeq.trim();
  const qr = registration.qr.trim();
  if (playerId && !/^\d+$/.test(playerId)) throw new Error("プレイヤーIDは数字で入力してください。");
  if (photoSeq && !/^\d+$/.test(photoSeq)) throw new Error("photo_seqは数字で入力してください。");
  if (!playerName) throw new Error("プレイヤー名を入力してください。");
  if (!qr || !/^[01]+$/.test(qr)) throw new Error("読み取ったQRデータが不正です。");
  const id = options.id ?? crypto.randomUUID();
  const createdAt = options.createdAt ?? new Date().toISOString();
  const tags: FriendCodeTags = registration.tags.not_web ? { not_web: true } : {};
  const code: FriendCode = {
    id,
    playerId,
    playerName,
    photoSeq,
    coordinate: registration.coordinate,
    qr,
    tags,
    imageUrl: registration.imageUrl.trim(),
    createdAt,
  };
  const response = await fetchWithTimeout(dataSourceGetUrl(source, "friend-qr-POST"), dataSourceRequestInit(source, {
      method: "friend-qr-POST",
      id,
      playerId,
      playerName,
      photo_seq: photoSeq,
      coordinateType: code.coordinate?.type ?? "",
      coordinateId: code.coordinate?.id ?? "",
      data: qr,
      tags: JSON.stringify(code.tags),
      imageUrl: code.imageUrl,
      createdAt,
  }), FRIEND_REQUEST_TIMEOUT_MS);
  await readApiResponse(response);
  return code;
}

export async function updateFriendCoordinate(
  source: UserDataSource,
  id: string,
  coordinate: FriendCoordinateReference,
): Promise<void> {
  if (!isDataSourceConfigured(source)) throw new Error("保存先が設定されていません。");
  const response = await fetchWithTimeout(dataSourceGetUrl(source, "friend-PUT"), dataSourceRequestInit(source, {
      method: "friend-PUT",
      id,
      coordinateType: coordinate?.type ?? "",
      coordinateId: coordinate?.id ?? "",
  }), FRIEND_REQUEST_TIMEOUT_MS);
  await readApiResponse(response);
}
